/* jshint esversion: 11 */

// Anmeldung per OAuth 2.0 (Matrix-Spec „OAuth 2.0 API“) und Verwaltung der Session.
//
// Welcher Authorization-Server zuständig ist, sagt der Homeserver über
// /_matrix/client/v1/auth_metadata. Die client_id ist pro Issuer statisch in MAS
// registriert; ein unbekannter Issuer ist ein Fehler.

import * as sdk from "matrix-js-sdk";
import {OAuth2, TokenRefresher} from "matrix-js-sdk";
import {decryptJson, deletePickleKey, encryptJson, randomStorageKey, storageKeyBytes} from "./pickle-key.js";

const ZIEGE_HOMESERVER_URL = "https://matrix.ziege.social";

const CLIENT_IDS_BY_ISSUER = {
    "https://matrix.ziege.social/auth/": "01M3N06TRNYV2Q5V0HK8VX3SPE",
};

const SESSION_KEY = "ziege_session";
const PENDING_LOGIN_PREFIX = "ziege_pending_login:";
const PENDING_LOGIN_TTL = 30 * 60 * 1000;
const LOGIN_HISTORY_PREFIX = "ziege_login_history:";
// Chrome begrenzt history.length auf 50; ab dort stimmt die Zählung nicht mehr.
const HISTORY_LENGTH_LIMIT = 50;

// Schlüssel der Anmeldung per legacy SSO (m.login.token)
const LEGACY_KEYS = ["ziege_access_token", "ziege_user_id", "ziege_device_id"];


// Die Redirect-URI muss exakt einer der in MAS registrierten entsprechen.
function redirectUri() {
    return window.location.origin + window.location.pathname;
}

function clientIdForIssuer(issuer) {
    const clientId = CLIENT_IDS_BY_ISSUER[issuer];
    if (!clientId) {
        throw new Error(`Für den Anmelde-Server ${issuer} ist keine Ziege-Anmeldung eingerichtet.`);
    }
    return clientId;
}

async function fetchAuthMetadata(homeserverUrl) {
    return await sdk.createClient({baseUrl: homeserverUrl}).getAuthMetadata();
}


// Gespeichert wird die Session ohne Klartext-Geheimnisse: Access-Token, Refresh-Token
// und der Schlüssel des Krypto-Stores stehen verschlüsselt in „secrets“.
function readStoredSession() {
    try {
        return JSON.parse(localStorage.getItem(SESSION_KEY));
    } catch (e) {
        console.error("Session nicht lesbar: ", e);
        return null;
    }
}

export async function loadSession() {
    const stored = readStoredSession();
    if (!stored?.secrets) {
        return null;
    }
    const secrets = await decryptJson(stored.secrets);
    if (!secrets) {
        return null;
    }
    const {secrets: _encrypted, ...session} = stored;
    return {...session, ...secrets};
}

async function saveSession(session) {
    const {accessToken, refreshToken, storageKey, ...rest} = session;
    const secrets = await encryptJson({accessToken, refreshToken, storageKey});
    localStorage.setItem(SESSION_KEY, JSON.stringify({...rest, secrets}));
}

// Ob eine Sitzung gespeichert ist, ohne sie zu entschlüsseln.
export function hasStoredSession() {
    return localStorage.getItem(SESSION_KEY) !== null;
}

// Schlüssel für initRustCrypto({storageKey})
export function cryptoStorageKey(session) {
    return storageKeyBytes(session.storageKey);
}

// Name des Krypto-Stores (cryptoDatabasePrefix von matrix-js-sdk). Jedes Gerät hat
// seinen eigenen; Sitzungen, die ohne Namen gespeichert wurden, nutzen den Vorgabewert
// des SDK, unter dem ihre Schlüssel liegen.
export const DEFAULT_CRYPTO_STORE_PREFIX = "matrix-js-sdk";

export function cryptoStorePrefix(session) {
    return session.cryptoStorePrefix ?? DEFAULT_CRYPTO_STORE_PREFIX;
}

function oauthForSession(session) {
    return new OAuth2(session.authMetadata, {
        clientId: session.clientId,
        deviceId: session.deviceId,
        redirectUri: session.redirectUri,
    });
}


// Leitet zum Authorization-Server weiter. Der PKCE-Code-Verifier und die übrigen
// Parameter werden unter dem state abgelegt, bis der Browser zurückkommt.
// localStorage statt sessionStorage, weil der Rücksprung bei installierten PWAs
// nicht zwingend im selben Tab landet.
export async function startLogin(homeserverUrl = ZIEGE_HOMESERVER_URL) {
    const authMetadata = await fetchAuthMetadata(homeserverUrl);
    const oauth = new OAuth2(authMetadata, {
        clientId: clientIdForIssuer(authMetadata.issuer),
        redirectUri: redirectUri(),
    });
    const state = crypto.randomUUID();

    localStorage.setItem(PENDING_LOGIN_PREFIX + state, JSON.stringify({
        createdAt: Date.now(),
        homeserverUrl,
        issuer: authMetadata.issuer,
        ...oauth.context,
    }));

    const url = await oauth.generateAuthorizationCodeGrantUrl(state);
    rememberHistoryPosition(state);
    window.location.assign(url);
}

// Die Seiten des Authorization-Servers bleiben nach der Anmeldung in der History;
// Zurück führte auf ein schon abgeschlossenes Formular. Nach der Anmeldung springt
// die App deshalb auf den Eintrag zurück, von dem aus die Anmeldung begann, und die
// Seiten des Authorization-Servers liegen in Vorwärts-Richtung.
//
// pushState verwirft etwaige Vorwärts-Einträge; danach ist history.length genau die
// Position hinter dem Eintrag, auf den zurückgesprungen wird. Der Klick auf
// „Zur Anmeldung“ ist die Nutzeraktion, die Browser für pushState erwarten.
// sessionStorage gehört zum Tab: Landet der Rücksprung in einem anderen Tab, gibt es
// keine Position und keinen Sprung.
function rememberHistoryPosition(state) {
    history.pushState(null, "");
    if (history.length < HISTORY_LENGTH_LIMIT) {
        sessionStorage.setItem(LOGIN_HISTORY_PREFIX + state, String(history.length));
    }
}

// Springt nach abgeschlossener Anmeldung auf den Eintrag vor der Anmeldung zurück.
// Liefert false, wenn das nicht geht; dann läuft die App auf der aktuellen Seite weiter.
export function returnToLoginStart(state) {
    const key = LOGIN_HISTORY_PREFIX + state;
    const lengthAtStart = Number(sessionStorage.getItem(key));
    sessionStorage.removeItem(key);
    const steps = history.length - lengthAtStart + 1;
    if (!lengthAtStart || history.length >= HISTORY_LENGTH_LIMIT || steps < 2) {
        return false;
    }
    history.go(-steps);
    return true;
}

function takePendingLogin(state) {
    const now = Date.now();
    let found = null;
    for (const key of Object.keys(localStorage)) {
        if (!key.startsWith(PENDING_LOGIN_PREFIX)) {
            continue;
        }
        const pending = JSON.parse(localStorage.getItem(key));
        if (key === PENDING_LOGIN_PREFIX + state && now - pending.createdAt < PENDING_LOGIN_TTL) {
            found = pending;
        }
        if (key === PENDING_LOGIN_PREFIX + state || now - pending.createdAt >= PENDING_LOGIN_TTL) {
            localStorage.removeItem(key);
        }
    }
    return found;
}

// Liefert die Parameter des Rücksprungs vom Authorization-Server (response_mode=fragment),
// oder null, wenn die aktuelle URL keiner ist.
export function authorizationResponse() {
    const params = new URLSearchParams(window.location.hash.slice(1));
    if (!params.has("state") || !(params.has("code") || params.has("error"))) {
        return null;
    }
    return params;
}

// Schließt die Anmeldung nach dem Rücksprung ab und legt die Session an.
export async function completeLogin(params) {
    window.history.replaceState(null, "", window.location.pathname);

    if (params.has("error")) {
        throw new Error(`Anmeldung abgelehnt: ${params.get("error_description") || params.get("error")}`);
    }

    const pending = takePendingLogin(params.get("state"));
    if (!pending) {
        throw new Error("Anmeldung abgelaufen oder unbekannt. Bitte neu anmelden.");
    }

    const authMetadata = await fetchAuthMetadata(pending.homeserverUrl);
    if (authMetadata.issuer !== pending.issuer) {
        throw new Error("Der Anmelde-Server hat sich während der Anmeldung geändert.");
    }

    const oauth = new OAuth2(authMetadata, pending);
    const tokens = await oauth.completeAuthorizationCodeGrant(params.get("code"));

    const whoami = await sdk.createClient({
        baseUrl: pending.homeserverUrl,
        accessToken: tokens.access_token,
    }).whoami();
    if (whoami.device_id !== pending.deviceId) {
        throw new Error("Der Homeserver meldet ein anderes Gerät als angefordert.");
    }

    const session = {
        homeserverUrl: pending.homeserverUrl,
        authMetadata,
        clientId: pending.clientId,
        redirectUri: pending.redirectUri,
        userId: whoami.user_id,
        deviceId: whoami.device_id,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        storageKey: randomStorageKey(),
        cryptoStorePrefix: `ziege-${whoami.device_id}`,
    };
    await saveSession(session);
    return session;
}


// Optionen für sdk.createClient(); erneuerte Tokens landen in der gespeicherten Session.
export function clientOptsForSession(session) {
    const refresher = new TokenRefresher(oauthForSession(session), async ({accessToken, refreshToken}) => {
        const current = await loadSession();
        if (current && current.deviceId === session.deviceId) {
            await saveSession({...current, accessToken, refreshToken});
        }
    });

    // Refresh-Tokens gelten nur einmal. Ist die App in mehreren Tabs offen, darf
    // nur einer erneuern; die anderen übernehmen danach dessen Tokens. Ein zweiter
    // Versuch mit dem verbrauchten Token ließe MAS die Session beenden.
    const tokenRefreshFunction = (refreshToken) => {
        const refreshOnce = async () => {
            const current = await loadSession();
            if (current && current.deviceId === session.deviceId && current.refreshToken !== refreshToken) {
                return {accessToken: current.accessToken, refreshToken: current.refreshToken};
            }
            return refresher.tokenRefreshFunction(refreshToken);
        };
        return navigator.locks ? navigator.locks.request("ziege-token-refresh", refreshOnce) : refreshOnce();
    };

    return {
        baseUrl: session.homeserverUrl,
        userId: session.userId,
        deviceId: session.deviceId,
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
        tokenRefreshFunction,
    };
}

// Beendet die Session beim Authorization-Server. Das Widerrufen des Refresh-Tokens
// beendet bei MAS die ganze Session samt Access-Token und Matrix-Gerät.
export async function revokeSession(session) {
    const current = (await loadSession()) ?? session;
    const oauth = oauthForSession(current);
    try {
        await oauth.revokeToken(current.refreshToken, "refresh_token");
    } catch (e) {
        console.error("Widerruf des Refresh-Tokens fehlgeschlagen: ", e);
        await oauth.revokeToken(current.accessToken, "access_token");
    }
}

export async function forgetSession() {
    localStorage.removeItem(SESSION_KEY);
    await deletePickleKey();
}


// Eine gespeicherte Session, die sich nicht mehr verwenden lässt, wird verworfen:
// Tokens im Klartext samt unverschlüsseltem Krypto-Store (ältere Version dieses
// Clients) oder Geheimnisse, deren Schlüssel verloren ist. Klartext-Tokens werden
// vorher widerrufen. Liefert true, wenn etwas verworfen wurde.
export async function discardUnusableSession() {
    const stored = readStoredSession();
    if (!stored || await loadSession()) {
        return false;
    }

    if (stored.refreshToken) {
        try {
            await oauthForSession(stored).revokeToken(stored.refreshToken, "refresh_token");
        } catch (e) {
            console.warn("Widerruf der alten Session fehlgeschlagen: ", e);
        }
    }
    await sdk.createClient({baseUrl: stored.homeserverUrl}).clearStores({cryptoDatabasePrefix: cryptoStorePrefix(stored)});
    await forgetSession();
    return true;
}


// Eine Anmeldung aus der Zeit vor OAuth wird abgemeldet und samt Krypto-Store
// verworfen; danach ist eine neue Anmeldung nötig.
export async function discardLegacySession() {
    const accessToken = localStorage.getItem("ziege_access_token");
    if (!accessToken) {
        return;
    }

    const client = sdk.createClient({
        baseUrl: ZIEGE_HOMESERVER_URL,
        accessToken,
        userId: localStorage.getItem("ziege_user_id"),
        deviceId: localStorage.getItem("ziege_device_id"),
    });
    try {
        await client.logout(true);
    } catch (e) {
        console.warn("Abmelden der alten Session fehlgeschlagen: ", e);
    }
    await client.clearStores();

    for (const key of LEGACY_KEYS) {
        localStorage.removeItem(key);
    }
}
